import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type { Restaurant } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { ROLES } from '@/shared/constants/roles'

interface Props {
  restaurants: Array<Restaurant>
  isLoading?: boolean
  onEdit: (r: Restaurant) => void
}

export function RestaurantTable({ restaurants, isLoading, onEdit }: Props) {
  const { t: tRestaurants } = useTranslation('restaurants')
  const { t: tCommon } = useTranslation('common')
  const { can } = usePermissions()
  const canManage = can(ACTIONS.RESTAURANT_MANAGE)
  const canSchedule = can(ACTIONS.RESTAURANT_SCHEDULING_MANAGE)
  const canEdgeSettings = can(ACTIONS.RESTAURANT_EDGE_SETTINGS_MANAGE)
  const canVoidReasons = can(ACTIONS.POS_VOID_REASON_VIEW)
  const canEntnahmeReasons = can(ACTIONS.ENTNAHME_REASON_VIEW)
  const canEsl = can(ACTIONS.RESTAURANT_ESL_MANAGE)
  // Platform staff only: the Viva config carries webbill's commercial terms and credentials.
  const { hasRole } = useAuth()
  const canPayments = hasRole(ROLES.SUPER_ADMIN)
  const canBranding = can(ACTIONS.RESTAURANT_BRANDING_MANAGE)

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">{tRestaurants('table.id')}</TableHead>
            <TableHead>{tRestaurants('table.code')}</TableHead>
            <TableHead>{tRestaurants('table.name')}</TableHead>
            <TableHead>{tRestaurants('table.timezone')}</TableHead>
            <TableHead>{tRestaurants('table.onlineOrdering')}</TableHead>
            <TableHead className="text-right">
              {tRestaurants('table.defaultBuffer')}
            </TableHead>
            <TableHead className="w-56"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7}>{tRestaurants('table.loading')}</TableCell>
            </TableRow>
          ) : restaurants.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>{tRestaurants('table.empty')}</TableCell>
            </TableRow>
          ) : (
            restaurants.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-sm">{r.id}</TableCell>
                <TableCell className="font-mono text-sm">{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="font-mono text-sm">
                  {r.timezone ?? ''}
                </TableCell>
                <TableCell>
                  {r.onlineOrderingEnabled
                    ? tCommon('enabled')
                    : tCommon('disabled')}
                </TableCell>
                <TableCell className="text-right">
                  {r.defaultSafetyBufferQty ?? 0}
                </TableCell>
                <TableCell className="text-right space-y-1">
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(r)}>
                      {tRestaurants('table.edit')}
                    </Button>
                  )}
                  <div className="flex items-center justify-end gap-3 text-sm">
                    {canSchedule && (
                      <Link
                        to="/restaurants/$id/scheduling"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.scheduling')}
                      </Link>
                    )}
                    {canVoidReasons && (
                      <Link
                        to="/restaurants/$id/void-reasons"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.voidReasons')}
                      </Link>
                    )}
                    {canEntnahmeReasons && (
                      <Link
                        to="/restaurants/$id/entnahme-reasons"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.entnahmeReasons')}
                      </Link>
                    )}
                    {canEdgeSettings && (
                      <Link
                        to="/restaurants/$id/edge-settings"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.edgeSettings')}
                      </Link>
                    )}
                    {canManage && (
                      <Link
                        to="/restaurants/$id/purchase-ordering"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.purchaseOrdering')}
                      </Link>
                    )}
                    {canEsl && (
                      <Link
                        to="/restaurants/$id/esl-config"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.esl')}
                      </Link>
                    )}
                    {canPayments && (
                      <Link
                        to="/restaurants/$id/payments"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.payments')}
                      </Link>
                    )}
                    {canBranding && (
                      <Link
                        to="/restaurants/$id/kiosk-branding"
                        params={{ id: String(r.id) }}
                        className="underline"
                      >
                        {tRestaurants('table.kioskBranding')}
                      </Link>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
